import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { blogPosts } from "@/data/blogPosts";
import { SchemaMarkup, generateBreadcrumbSchema } from "@/components/SchemaMarkup";
import { AppStoreCTA } from "@/components/AppStoreCTA";

const Blog = () => {
  return (
    <>
      <Helmet>
        <title>15 Expert Travel Tips 2025 - Save Hours at Airport Security</title>
        <meta name="description" content="✈️ Proven strategies to breeze through TSA in under 10 minutes. Insider tricks from frequent flyers and expert airport guides." />
        <meta name="keywords" content="airport travel tips 2025, TSA security hacks, fast airport security tips, travel expert advice, airport time-saving tricks, frequent flyer tips" />
        <link rel="canonical" href="https://airportbuddy.app/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Airport Buddy Blog — Expert TSA & Travel Tips" />
        <meta property="og:description" content="Expert airport guides, TSA hacks, and travel tips to help you breeze through security." />
        <meta property="og:url" content="https://airportbuddy.app/blog" />
      </Helmet>

      <SchemaMarkup schema={generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" }
      ])} />

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 py-8">
          <Button asChild variant="ghost" className="mb-6 min-h-[44px]">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Back to Home
            </Link>
          </Button>

          <div className="max-w-4xl mx-auto space-y-8">
            <header className="text-center space-y-4">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
                Travel Tips & Guides
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground">
                Expert advice to make your airport experience smoother
              </p>
            </header>

            <div className="grid gap-6">
              {blogPosts.map((post) => (
                <Card key={post.slug} className="shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5">
                  <CardHeader>
                    <CardTitle className="text-xl md:text-2xl">
                      <Link 
                        to={`/blog/${post.slug}`} 
                        className="hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
                      >
                        {post.title}
                      </Link>
                    </CardTitle>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-2">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="h-4 w-4" aria-hidden="true" />
                        <time dateTime={post.date}>
                          {new Date(post.date).toLocaleDateString('en-US', { 
                            month: 'long', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </time>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="h-4 w-4" aria-hidden="true" />
                        {post.readTime}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-base text-muted-foreground mb-4">
                      {post.description}
                    </p>
                    <Button asChild variant="outline" className="min-h-[44px]">
                      <Link to={`/blog/${post.slug}`}>
                        Read More
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <AppStoreCTA
              placement="blog_index"
              headline="Skip the reading — get the alerts"
              subline="Airport Buddy for iPhone shows live TSA waits and tells you when to leave. Free."
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Blog;
